/**
 * AdminHealthPage - 5-card status grid (Phase 12.6).
 *
 * SPEC §9 AdminHealthPage: one card per external component (postgres,
 * redis, microsoft_graph, spaces, buyist_api) colored by status. The header
 * shows the snapshot's checked_at timestamp; "Re-check" triggers a
 * fresh GET.
 */
import { Alert, Button, Spinner } from "@/components/ui";
import { HealthCard } from "@/components/admin/HealthCard";
import { useAdminHealth } from "@/hooks/useAdminHealth";
import { usePageTitle } from "@/hooks/usePageTitle";

export function AdminHealthPage(): JSX.Element {
  usePageTitle("System health");

  const { data, isLoading, error, refetch, isFetching } = useAdminHealth();

  return (
    <div className="mx-auto w-full max-w-6xl">
      <header className="mb-4 flex items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">
            System health
          </h1>
          {data && (
            <p className="mt-1 text-sm text-neutral-600">
              Last checked: {new Date(data.checked_at).toLocaleString()}
            </p>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void refetch()}
          isLoading={isFetching}
        >
          Re-check
        </Button>
      </header>

      {isLoading && (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      )}

      {error && (
        <Alert variant="error" title="Failed to load health snapshot">
          {error instanceof Error ? error.message : "Unknown error."}
        </Alert>
      )}

      {data && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.components.map((component) => (
            <HealthCard key={component.name} component={component} />
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminHealthPage;
